import React, { useEffect, useMemo, useState } from 'react';
import { useTenantContext } from '../../context/TenantContext';

// Chargement dynamique des templates d'en-tête ADMIN
// Convention: placez vos headers admin dans /src/templates/admin-headers/
const adminHeaderModules = import.meta.glob('/src/templates/admin-headers/**/*.{tsx,jsx,ts,js}');

interface AdminHeaderSlotProps {
  // Permet au parent (AdminLayout) de savoir si un header custom est chargé et/ou en chargement
  onStatusChange?: (hasCustom: boolean, loading: boolean) => void;
}

export const AdminHeaderSlot: React.FC<AdminHeaderSlotProps> = ({ onStatusChange }) => {
  const { tenant } = useTenantContext();

  const selectedPath = useMemo(() => {
    const templates = (tenant?.config?.templates || {}) as any;
    const adminHeaders = (templates.admin_headers || {}) as Record<string, string>;
    return adminHeaders.default || '';
  }, [tenant]);

  const [HeaderComponent, setHeaderComponent] = useState<React.ComponentType | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [tenantLoading, setTenantLoading] = useState(!tenant);

  // Suivre le chargement du tenant
  useEffect(() => {
    setTenantLoading(!tenant);
  }, [tenant]);

  // Charger le header admin dynamiquement
  useEffect(() => {
    let mounted = true;

    const notify = (hasCustom: boolean, loading: boolean) => {
      try { onStatusChange && onStatusChange(hasCustom, loading); } catch {}
    };

    if (selectedPath && adminHeaderModules[selectedPath]) {
      setLoadingTemplate(true);
      notify(false, true);
      const loader = adminHeaderModules[selectedPath] as () => Promise<{ default: React.ComponentType }>;
      loader()
        .then((mod) => {
          if (!mounted) return;
          setHeaderComponent(() => mod.default);
          notify(true, false);
        })
        .catch((err) => {
          console.error('Erreur de chargement du header admin:', err);
          if (!mounted) return;
          setHeaderComponent(null);
          notify(false, false);
        })
        .finally(() => {
          if (mounted) setLoadingTemplate(false);
        });
    } else {
      setHeaderComponent(null);
      setLoadingTemplate(false);
      notify(false, false);
    }

    return () => {
      mounted = false;
    };
  }, [selectedPath]);

  // Pendant le chargement, ne rien afficher. Si pas de header custom, on laisse le parent rendre son fallback
  if (tenantLoading || loadingTemplate) return null;
  if (HeaderComponent) return <HeaderComponent />;
  return null;
};

export default AdminHeaderSlot;
